import { Zap, Activity, AlertCircle, CheckCircle, Clock } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';

const ChargerStatus = ({ status }) => {
  const config = {
    available: {
      icon: CheckCircle,
      color: 'bg-emerald-50 text-emerald-700 border-emerald-200',
      label: 'Disponível',
      iconColor: 'text-emerald-600'
    },
    in_use: {
      icon: Activity,
      color: 'bg-blue-50 text-blue-700 border-blue-200',
      label: 'Em Uso',
      iconColor: 'text-blue-600'
    },
    out_of_order: {
      icon: AlertCircle,
      color: 'bg-red-50 text-red-700 border-red-200',
      label: 'Avariado',
      iconColor: 'text-red-600'
    },
    maintenance: {
      icon: Clock,
      color: 'bg-amber-50 text-amber-700 border-amber-200',
      label: 'Manutenção',
      iconColor: 'text-amber-600'
    }
  };

  const { icon: Icon, color, label, iconColor } = config[status] || config.available;

  return (
    <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border font-medium text-sm ${color}`}>
      <Icon size={16} className={iconColor} />
      {label}
    </div>
  );
};

export const ChargerSpecsCard = ({ charger }) => {
  return (
    <Card className="p-6">
      <div className="flex items-start justify-between mb-6">
        <div className="flex items-center gap-4">
          <div className="bg-blue-600 p-4 rounded-xl">
            <Zap className="text-white" size={32} />
          </div>
          <div>
            <h3 className="text-xl font-secondary font-bold text-slate-900">Especificações Técnicas</h3>
            <p className="text-sm text-slate-600 mt-1">Características do carregador</p>
          </div>
        </div>
        <ChargerStatus status={charger.status} />
      </div>

      <div className="grid grid-cols-2 gap-6">
        <div className="space-y-4">
          <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
            <p className="text-xs text-slate-600 mb-1">Potência Máxima</p>
            <p className="text-2xl font-secondary font-bold text-slate-900">{charger.power}</p>
          </div>
          
          <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
            <p className="text-xs text-slate-600 mb-1">Tipo de Conector</p>
            <div className="flex items-center gap-2 mt-2">
              {charger.connectors.map((connector) => (
                <Badge key={connector} variant="outline" className="font-medium">
                  {connector}
                </Badge>
              ))}
            </div>
          </div>

          <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
            <p className="text-xs text-slate-600 mb-1">Tipo de Corrente</p>
            <p className="text-lg font-semibold text-slate-900">{charger.currentType}</p>
          </div>
        </div>

        <div className="space-y-4">
          <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
            <p className="text-xs text-blue-700 mb-1">Total Carregamentos</p>
            <p className="text-2xl font-secondary font-bold text-blue-900">{charger.totalCharges}</p>
            <p className="text-xs text-blue-600 mt-1">Desde instalação</p>
          </div>

          <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
            <p className="text-xs text-emerald-700 mb-1">Energia Total Fornecida</p>
            <p className="text-2xl font-secondary font-bold text-emerald-900">{charger.totalKwh} kWh</p>
            <p className="text-xs text-emerald-600 mt-1">Consumo acumulado</p>
          </div>

          <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
            <p className="text-xs text-purple-700 mb-2">Taxa de Disponibilidade</p>
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm text-purple-900 font-medium">{charger.availability}%</span>
            </div>
            <Progress value={charger.availability} className="h-2" />
          </div>
        </div>
      </div>

      <div className="mt-6 pt-6 border-t border-slate-200">
        <div className="grid grid-cols-3 gap-4 text-center">
          <div>
            <p className="text-xs text-slate-600 mb-1">Instalação</p>
            <p className="text-sm font-medium text-slate-900">{charger.installationDate}</p>
          </div>
          <div>
            <p className="text-xs text-slate-600 mb-1">Última Manutenção</p>
            <p className="text-sm font-medium text-slate-900">{charger.lastMaintenance}</p>
          </div>
          <div>
            <p className="text-xs text-slate-600 mb-1">Próxima Manutenção</p>
            <p className="text-sm font-medium text-slate-900">{charger.nextMaintenance}</p>
          </div>
        </div>
      </div>

      {charger.status === 'in_use' && charger.currentSession && (
        <div className="mt-6 p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border-2 border-blue-200">
          <div className="flex items-center justify-between mb-3">
            <p className="font-medium text-blue-900">⚡ Carregamento em Progresso</p>
            <Badge className="bg-blue-600">Ativo</Badge>
          </div>
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div>
              <p className="text-blue-700">Tempo Decorrido</p>
              <p className="font-bold text-blue-900">{charger.currentSession.duration}</p>
            </div>
            <div>
              <p className="text-blue-700">Energia Fornecida</p>
              <p className="font-bold text-blue-900">{charger.currentSession.kwh} kWh</p>
            </div>
            <div>
              <p className="text-blue-700">Potência Atual</p>
              <p className="font-bold text-blue-900">{charger.currentSession.currentPower}</p>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
};
